/**
 * DashboardLayout - ticker-scoped dashboard tab strip.
 *
 * Refinements:
 *   - Horizontal tab strip using the refined Tabs primitive (line variant)
 *     so the layoutId-based animated underline works
 *   - onMouseEnter on each tab triggers prefetchRouteData for that sibling
 *     dashboard route (80ms debounce)
 *   - Back link to /analysis/:ticker preserved
 *   - Ticker context visible in the strip
 *
 * Do not add em-dashes anywhere.
 */

import React, { useRef, useCallback, useEffect } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { elevation, color } from '@/design-system/tokens'
import { prefetchRouteData } from '@/app/queryClient'

const TABS = [
  { path: 'quantitative',        label: 'Quantitative' },
  { path: 'credit-risk',         label: 'Credit Risk' },
  { path: 'insider-flow',        label: 'Insider Flow' },
  { path: 'governance',          label: 'Governance' },
  { path: 'event-timeline',      label: 'Event Timeline' },
  { path: 'options-market',      label: 'Options Market' },
  { path: 'macro-overlay',       label: 'Macro Overlay' },
  { path: 'attention-sentiment', label: 'Attention & Sentiment' },
]

function getActiveTab(pathname: string): string {
  for (const { path } of TABS) {
    if (pathname.endsWith(`/${path}`)) return path
  }
  return TABS[0].path
}

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { ticker } = useParams<{ ticker: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const t = ticker ?? ''

  const activeTab = getActiveTab(location.pathname)

  // Debounced prefetch on tab hover
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleTabHover = useCallback(
    (path: string) => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
      hoverTimer.current = setTimeout(() => {
        prefetchRouteData(`/dashboard/${t}/${path}`)
      }, 80)
    },
    [t],
  )
  const handleTabHoverEnd = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current)
      hoverTimer.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
    }
  }, [])

  const tier1 = elevation.tier1

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sticky sub-nav strip */}
      <nav
        aria-label="Dashboard sub-pages"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          ...tier1,
          borderBottom: tier1.border,
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          overflowX: 'auto',
        }}
      >
        {/* Back to analysis link */}
        <button
          onClick={() => navigate(`/analysis/${t}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            color: color.graySecondary,
            cursor: 'pointer',
            flexShrink: 0,
            marginRight: '8px',
            background: 'transparent',
            border: 'none',
            padding: '4px 0',
          }}
          aria-label="Back to analysis"
        >
          <ChevronLeft style={{ width: '12px', height: '12px' }} />
          Back
        </button>

        {/* Ticker label. Uses grayPrimary at full opacity to meet WCAG AA
            on tier1 sub-nav background (translucent graySecondary fell to
            ~2.06:1). */}
        <span
          style={{
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: color.grayPrimary,
            flexShrink: 0,
          }}
        >
          {t}
        </span>

        {/* Tab strip */}
        <Tabs value={activeTab} onValueChange={(val) => navigate(`/dashboard/${t}/${val}`)}>
          <TabsList variant="line" style={{ height: '40px', padding: 0 }}>
            {TABS.map(({ path, label }) => (
              <TabsTrigger
                key={path}
                value={path}
                onMouseEnter={() => handleTabHover(path)}
                onMouseLeave={handleTabHoverEnd}
                style={{ fontSize: '12px', padding: '0 12px' }}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </nav>

      {/* Page content */}
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  )
}
